import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'

import { css } from 'design'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import { TypingIndicators } from './typing_indicators'

export type TimelinesInfiniteScrollerProps = {
  topicId: any
  onLoadMore: () => void
  isFetching?: boolean
  /* firstFetchComplete: this prop is used to reinitialize the scroll position after the first fetch has completed.
   * Used to solve a problem where the scroll manager preserves the scroll position of an old timeline before the
   * timelines are explicitly fetched. This old timeline may have come from local storage (which we show to make
   * content available quicker), or arrived from a different request (e.g. meetings, tasks) */
  firstFetchComplete: boolean
} & React.HTMLAttributes<HTMLDivElement>

export const TimelinesInfiniteScroller = React.forwardRef<
  HTMLDivElement,
  TimelinesInfiniteScrollerProps
>(({ topicId, children, onLoadMore, isFetching, firstFetchComplete }, ref) => {
  const elementContainerRef = useRef<HTMLDivElement>(null)
  const loaderRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const scrollBoxRef = useRef<HTMLDivElement>(null)
  const scrollInitialized = useRef(false)
  const prevScrollData = useRef<any>({
    scrollHeight: 0,
    clientHeight: 0,
    scrollTop: 0,
  })
  const containerResizeObserver = useRef<ResizeObserver>()
  const childrenResizeObserver = useRef<ResizeObserver>()
  const [canJumpToPresent, setCanJumpToPresent] = useState(false)
  const offsetTops = useRef<{ [id: string]: number }>({})

  useLayoutEffect(() => {
    const container = scrollBoxRef.current
    if (container) {
      return () => {
        scrollInitialized.current = false
        prevScrollData.current = {
          scrollHeight: 0,
          clientHeight: 0,
          scrollTop: 0,
        }
        offsetTops.current = {}
        setCanJumpToPresent(false)
      }
    }
  }, [topicId, firstFetchComplete])

  useLayoutEffect(() => {
    const container = scrollBoxRef.current
    if (container) {
      const onScroll = () => {
        if (!scrollInitialized.current) {
          return
        }
        const { scrollHeight, clientHeight, scrollTop } = container
        prevScrollData.current = {
          scrollHeight,
          clientHeight,
          scrollTop,
        }
        if (scrollTop <= 300) {
          onLoadMore()
        }
        setCanJumpToPresent(scrollTop <= scrollHeight - clientHeight - 500)
      }
      container.addEventListener('scroll', onScroll)

      return () => {
        container.removeEventListener('scroll', onScroll)
      }
    }
  }, [onLoadMore])

  useLayoutEffect(() => {
    const container = scrollBoxRef.current
    if (container) {
      const onContainerResize = () => {
        const { scrollHeight, clientHeight, scrollTop } = container
        const { clientHeight: prevClientHeight } = prevScrollData.current
        if (!scrollInitialized.current) {
          // do not set scrollInitialized to true, the children resize observer will handle initialization
          container.scrollTop = scrollHeight - clientHeight
        } else {
          // preserve the scroll position when the client height of the container changes
          const diff = clientHeight - prevClientHeight
          container.scrollTop = Math.min(
            scrollTop - diff,
            scrollHeight - clientHeight,
          )
        }
      }
      const ro = new ResizeObserver(onContainerResize)
      ro.observe(container)
      containerResizeObserver.current = ro

      return () => {
        ro.disconnect()
        containerResizeObserver.current = undefined
      }
    }
  }, [topicId, firstFetchComplete])

  useLayoutEffect(() => {
    const container = scrollBoxRef.current
    if (container) {
      const onChildrenResize = () => {
        const newOffsetTops: any = Array.from(
          container.children[0].children,
        ).reduce((acc, child) => {
          const id = child.getAttribute('data-timeline-id')
          if (!id) {
            console.warn(
              'An element was found without a data-timeline-id attribute. Needed for scroll management.',
            )
          }
          // eslint-disable-next-line
          // @ts-ignore
          acc[id] = child.offsetTop
          return acc
        }, {})
        const { scrollHeight, clientHeight } = container
        const {
          scrollHeight: prevScrollHeight,
          clientHeight: prevClientHeight,
          scrollTop: prevScrollTop,
        } = prevScrollData.current
        const prevScrollBottom = prevScrollTop + prevClientHeight

        if (!scrollInitialized.current) {
          container.scrollTop = scrollHeight - clientHeight
          prevScrollData.current = {
            scrollHeight,
            clientHeight,
            scrollTop: container.scrollTop,
          }
          scrollInitialized.current = true
        } else if (prevScrollHeight === prevClientHeight) {
          container.scrollTop = scrollHeight - clientHeight
        } else {
          // preserve the scroll position, when children resize
          const { idAtScrollBottom, offset } = Object.keys(
            offsetTops.current,
          ).reduce(
            (acc: any, id) => {
              const offsetTop = offsetTops.current[id]
              if (
                offsetTop <= prevScrollBottom &&
                (!acc.offsetTop || offsetTop > acc.offsetTop)
              ) {
                acc = {
                  idAtScrollBottom: id,
                  offsetTop: offsetTop,
                  offset: prevScrollBottom - offsetTop,
                }
              }
              return acc
            },
            {
              idAtScrollBottom: undefined,
              offsetTop: undefined,
              offset: undefined,
            },
          )
          const newPosition = newOffsetTops[idAtScrollBottom]
          if (newPosition) {
            container.scrollTop = Math.min(
              newPosition - clientHeight + offset,
              scrollHeight - clientHeight,
            )
          }
        }

        const prevNearBottom =
          prevScrollTop >=
          prevScrollHeight - prevClientHeight - BOTTOM_THRESHOLD
        if (prevNearBottom) {
          // scroll to the bottom when new messages are received
          requestAnimationFrame(() => {
            container.scroll({
              top: scrollHeight - clientHeight,
              behavior: 'smooth',
            })
          })
        }

        offsetTops.current = newOffsetTops
      }
      const ro = new ResizeObserver(onChildrenResize)
      container.childNodes.forEach((child) => {
        // eslint-disable-next-line
        // @ts-ignore
        ro.observe(child)
      })
      childrenResizeObserver.current = ro

      return () => {
        ro.disconnect()
        childrenResizeObserver.current = undefined
      }
    }
  }, [topicId, firstFetchComplete])

  const jumpToPresent = useCallback(() => {
    const container = scrollBoxRef.current
    if (container) {
      const { scrollHeight, clientHeight } = container
      container.scroll({
        top: scrollHeight - clientHeight,
        behavior: 'smooth',
      })
    }
  }, [])

  return (
    <section
      /* this key assists the scroll manager by re-creating the DOM element when the selected topic changes,
      versus letting react reuse the same DOM element. Reusing the same DOM element sometimes persists unwanted
      properties such as scroll position, especially in iOS safari. */
      key={topicId}
      className="topic-elems"
      ref={mergeRefs([ref, scrollBoxRef])}
    >
      <div
        className="topic-elems__container"
        ref={elementContainerRef}
        role="list"
      >
        <div
          className={cn(
            'timelines-infinite-scroll-loader',
            isFetching && 'timelines-infinite-scroll-loader--fetching',
          )}
          ref={loaderRef}
          role="listitem"
          tabIndex={-1}
          data-timeline-id={'timelines-infinite-scroll-loader'}
        />

        {children}
        <TypingIndicators />

        <div
          className={cn(anchorStyles(), 'timelines__anchor')}
          ref={anchorRef}
          role="listitem"
          tabIndex={-1}
          data-timeline-id={'timelines__anchor'}
        />
      </div>
      {canJumpToPresent && (
        <button
          className={cn('timelines__jump', jumpToPresentButtonStyles())}
          onClick={jumpToPresent}
        >
          <span>{`You're viewing older messages`}</span>
          <Stack
            direction="row"
            gap="1"
            css={{
              alignItems: 'baseline',
              '> i': {
                fontSize: '$1',
              },
            }}
          >
            <span>Jump to present</span>
            <i className="fas fa-chevron-down" />
          </Stack>
        </button>
      )}
    </section>
  )
})

TimelinesInfiniteScroller.displayName = 'TimelinesInfiniteScroller'

const BOTTOM_THRESHOLD = 300

const anchorStyles = css({
  height: 24,
  width: '100%',
  marginTop: '0 !important',
  overflowAnchor: 'auto !important',
})

const jumpToPresentButtonStyles = css({
  width: '100%',
  position: 'sticky',
  bottom: -1,
  left: 0,
  zIndex: 2,

  display: 'flex',
  justifyContent: 'space-between',
  padding: '$2 $2 $2 $4',

  fontSize: '$2',

  backgroundColor: '$grey200',
  color: '$grey900',
  transition: 'background-color 120ms ease-out',

  border: 0,
  outline: 0,

  '&:hover': {
    backgroundColor: '$grey300',
  },
})
